import { ConnectionManagerClient } from "./connection-manager";
import {
  API_STATUS,
  API_RESPONSE_CODES,
  CONNECTION_MANAGER_ENV,
  DEFAULT_CONNECTION_FROM,
  SOURCE,
  MAX_RETRIES,
} from "../constants/config";
import { IRequestParams, IResponse, IApiError } from "./types";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";

/**
 * Main request function for connection manager API calls
 * Handles initialization, retry logic, and error handling
 *
 * @template TResponse - Expected response type
 * @template TRequest - Request data type
 * @param params - Request parameters
 * @returns Promise with API response data
 * @throws Error if request fails after retries
 *
 * @example
 * ```typescript
 * const response = await request<OffersResponse>({
 *   operationId: OPERATION_IDS.offers,
 *   requestData: { mobileNo, sourceId: '3' },
 * });
 * ```
 */

const getPromptResponse = (response: string) => {
  try {
    return JSON.parse(response) as IResponse<unknown>;
  } catch (error) {
    throw new Error(JSON.stringify(response));
  }
};

export async function request<TResponse, TRequest = unknown>(
  params: IRequestParams<TRequest>
): Promise<TResponse> {
  let retryCount = 0;
  const client = ConnectionManagerClient.getInstance();

  while (retryCount <= MAX_RETRIES) {
    try {
      const source = isOpenInApp() ? SOURCE.APP : SOURCE.WEB;

      const {
        operationId,
        requestData,
        connectionFrom = DEFAULT_CONNECTION_FROM,
        additionalHeaders = {},
      } = params;

      // Initialize connection manager library
      await client.initLib(source, CONNECTION_MANAGER_ENV);

      // Call API through connection manager gateway
      const response = await client.callApi<TResponse, TRequest>(
        operationId,
        requestData,
        connectionFrom,
        additionalHeaders,
        source
      );

      if (typeof response === "string") {
        const parsedResponse = getPromptResponse(response);
        return parsedResponse as unknown as TResponse;
      }

      // Check if request was successful
      if (response.STATUS === API_STATUS.SUCCESS) {
        return response.API_RESPONSE as TResponse;
      }

      // Request failed with error status
      throw new Error(JSON.stringify(response));
    } catch (error: unknown) {
      const apiError = error as IApiError;

      // Retry if decryption error (status code 5005)
      if (
        apiError?.STATUS === API_STATUS.ERROR &&
        apiError?.API_RESPONSE?.statusCode === API_RESPONSE_CODES.STATUS_CODE_5005
      ) {
        if (retryCount < MAX_RETRIES) {
          console.warn(
            `[Connection Manager] Decryption error, retrying... Attempt ${retryCount + 1}/${MAX_RETRIES}`,
            {
              operationId: params.operationId,
            }
          );
          // Clear cached operation list data on decryption error
          if (typeof localStorage !== "undefined") {
            localStorage.removeItem("operationListData");
          }
          retryCount++;
          continue;
        }
      }

      // No retry needed or max retries exceeded
      throw error;
    }
  }

  // Should never reach here, but TypeScript needs it
  throw new Error(
    `[Connection Manager] Max retries (${MAX_RETRIES}) exceeded for operation: ${params.operationId}`
  );
}

/**
 * Request with custom error handling
 * Returns null on error instead of throwing
 *
 * @template TResponse - Expected response type
 * @template TRequest - Request data type
 * @param params - Request parameters
 * @returns Promise with API response data or null on error
 *
 * @example
 * ```typescript
 * const response = await requestSafe<OffersResponse>({
 *   operationId: OPERATION_IDS.offers,
 *   requestData: { mobileNo, sourceId: '3' },
 * });
 *
 * if (response === null) {
 *   // Handle error
 * }
 * ```
 */
export async function requestSafe<TResponse, TRequest = unknown>(
  params: IRequestParams<TRequest>
): Promise<TResponse | null> {
  try {
    return await request<TResponse, TRequest>(params);
  } catch (error) {
    console.error(
      `[Connection Manager] Request failed for operation: ${params.operationId}`,
      error
    );
    return null;
  }
}
